import "@fortawesome/fontawesome-free/css/all.css";
import "keen-slider/keen-slider.min.css";
import "video-react/dist/video-react.css";
import * as React from "react";
import { ThemeProvider } from "@emotion/react";
import type { AppProps } from "next/app";
import theme from "../styles/theme";
import GlobalCSS from "../styles/utils/global.emotion";
import ResetCSS from "../styles/utils/reset.emotion";
import TypograthCSS from "../styles/utils/typograth.emotion";
import { DefaultSeo } from "next-seo";
import SEO from "../../next-seo.config";
import NextNProgress from "nextjs-progressbar";

import { useRouter } from "next/router";
import PageLoadingComponent from "../components/page-loading.component";
import AlertProvider from "../context/alert.context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import GlobalProvider from "../context/global.context";
import Script from "next/script";
import CartProvider from "../context/cart.context";
import { createStore, StateMachineProvider } from "little-state-machine";

import { Product } from "../models/product.model";
import { Toaster } from "react-hot-toast";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const queryClient = new QueryClient();

const getStorageType = () => {
  return typeof window !== "undefined" ? localStorage : undefined;
};

createStore(
  {
    order: {
      name: "",
      cpf: "",
      email: "",
      phone: "",
      paymentMethod: "",
    },
    address: {
      cep: "",
      state: "",
      city: "",
      neighborhood: "",
      street: "",
      number: "",
      complement: "",
    },
    freightValue: {
      deliveryForecast: "",
      value: 0,
    },
    installments: 1,
  },
  { storageType: getStorageType() },
);

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  const [isReady, setReady] = React.useState(false);

  React.useEffect(() => {
    router.isReady && setReady(true);
  }, [router.isReady]);

  const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

  return (
    <>
      {/*
        Aviso de cookies da AdOpt. No GTM o template da AdOpt roda no gatilho
        de Inicialização do consentimento apenas para definir os defaults do
        Google Consent Mode v2, com "instalar banner" DESMARCADO — quem carrega
        o banner e envia o update de consentimento é este injector. Manter os
        dois instalando o banner faz o aviso aparecer duplicado.
      */}
      <Script
        id="goadopt"
        src="//tag.goadopt.io/injector.js?website_code=db32c42d-ec98-43c0-abe6-50299fb424dc"
        className="adopt-injector"
        strategy="afterInteractive"
      />
      <NextNProgress color="#aba386" />
      <DefaultSeo {...SEO} />
      <QueryClientProvider client={queryClient}>
        <StateMachineProvider>
          <GlobalProvider>
            <ThemeProvider theme={theme}>
              <ResetCSS />
              <GlobalCSS />
              <TypograthCSS
                fontStyle={{
                  fontFamily: "'Montserrat', sans-serif",
                }}
              />
              <PageLoadingComponent active={!isReady} />
              <AlertProvider>
                <CartProvider>
                  <GoogleReCaptchaProvider
                    reCaptchaKey={reCaptchaKey}
                    scriptProps={{
                      async: true,
                      defer: true,
                      appendTo: "head",
                      nonce: undefined,
                    }}
                  >
                    <Component {...pageProps} />
                  </GoogleReCaptchaProvider>
                </CartProvider>
              </AlertProvider>
              <Toaster position="bottom-right" />
            </ThemeProvider>
          </GlobalProvider>
        </StateMachineProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}
